import { Spin } from "antd";
import i18n from "i18next";
import type { FC } from "react";
import { useCallback, useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router";

import { FormManager } from "../../../../../components/Form/FormManager";
import { useLoadFormDefinitionQuery } from "../../../../../components/Form/services/server/serverApi";
import { FormLayoutType } from "../../../../../components/Form/types";
import type { FormProps } from "../../../../../components/Form/types/FormProps";
import { showSuccessNotification } from "../../../../../components/NotificationAlerts";
import { ResourceName } from "../../../../../core/router/config/ResourceName";
import { BASE_PATH } from "../../../../../core/router/types/routerTypes";
import { useGlobalConfigurationLoadQuery, useGlobalConfigurationUpdateMutation } from "../../services/server/GlobalConfigurationApi";
import type { GlobalConfigurationEntity } from "../../types/GlobalConfigurationEntity";

export const GlobalConfigurationEditForm: FC<FormProps> = ({ runSubmit }) => {
    const navigate = useNavigate();
    const { data: formDefinition, isFetching: configIsFetching } = useLoadFormDefinitionQuery("global-configuration");

    const [formData, setFormData] = useState<GlobalConfigurationEntity>({} as GlobalConfigurationEntity);
    const { data: loadFormData, isFetching: loadFormIsInProgress } = useGlobalConfigurationLoadQuery();

    useEffect(() => {
        if (loadFormData) {
            setFormData(loadFormData);
        }
    }, [loadFormData, setFormData]);

    const onChangeCallback = useCallback(setFormData, [setFormData]);

    const [saveFormData, { isLoading: saveFormIsInProgress }] = useGlobalConfigurationUpdateMutation();
    const dispatch = useDispatch();
    const onValidatedCallback = useCallback(
        async (valid: boolean) => {
            if (!valid || !formData) {
                return;
            }

            const result = await saveFormData({ data: formData });

            const hasError = "error" in result;
            if (!hasError) {
                dispatch(
                    showSuccessNotification({
                        text: i18n.t("common.formUpdateSuccessNoId", {
                            resource: "Global Configuration",
                        }),
                        autoCloseDisabled: false,
                    }),
                );
                navigate(`${BASE_PATH}/${ResourceName.GLOBAL_CONFIGURATIONS}`);
            }
        },
        [saveFormData, formData, navigate, dispatch],
    );

    return (
        <Spin spinning={saveFormIsInProgress || configIsFetching || loadFormIsInProgress}>
            {formDefinition?.formConfiguration && (
                <FormManager<GlobalConfigurationEntity>
                    name="GlobalConfigurationEditForm"
                    formData={formData}
                    formLayout={FormLayoutType.DEFAULT}
                    formDefinition={formDefinition.formConfiguration}
                    onChange={onChangeCallback}
                    shouldValidate={runSubmit}
                    onValidated={onValidatedCallback}
                />
            )}
        </Spin>
    );
};
